import { useLazyQuery, useQuery } from "@apollo/client";
import { Listbox, Transition } from "@headlessui/react";
import { addDays, format, startOfWeek } from "date-fns";
import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uid } from "uuid";
import {
  GET_APPOINTMENT_TYPE_TEMPLATES,
  GET_AVAILABLE_SLOTS,
  GET_CONSUMER_APPOINTMENT_EVENT_SLOTS,
} from "../../apollo";
import { IEventSlot } from "../../interfaces";
import dateService, {
  API_DATE_FORMAT,
  USER_TIME_FORMAT_12H,
} from "../../services/date.service";
import useAvailabilityStore from "../../store/availability/availability.store";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const ProviderResult = () => {
  const navigate = useNavigate();

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [allServiceProviders, setAllServiceProviders] = useState([]);
  const [week, setWeek] = useState("");
  const [daysinWeek, setDaysinWeek] = useState([]);
  const [availableSlots, setAvailableSlots] = useState<IEventSlot[]>(null);

  const [selectedSlot, setSelectedSlot] = useState<IEventSlot>(),
    [dataSource, setDataSource] = useState<Record<string, IEventSlot[]>>({}),
    [selectedDate, setSelectedDate] = useState<string>(); // YYYY-MM-DD

  const [visitType, preferredDate, patientLocation, visitReason, specialty] =
    useAvailabilityStore((state) => [
      state.visitType,
      state.preferredDate,
      state.patientLocation,
      state.visitReason,
      state.specialty,
    ]);

  useEffect(() => {
    console.log("preferredDate", preferredDate);
    if (preferredDate === "Next Available") {
      setSelectedProvider("anyProvider");
    }
  }, [preferredDate]);

  const handleProceed = () => {
    navigate("/book-appointment", {
      state: { selectedSlot, selectedProvider },
    });
  };

  useEffect(() => {
    // get todays week

    const today = new Date();
    const week = format(today, "yyyy-'W'II");
    setWeek(week);
    get7DaysInWeek();
  }, []);

  const onChange = (week) => {
    setWeek(week);
  };

  function getDaysInWeek(weekInput) {
    // Split the input into year and ISO week parts
    const [yearStr, weekStr] = weekInput.split("-W");
    const year = parseInt(yearStr, 10);
    const isoWeek = parseInt(weekStr, 10);

    // Calculate the date of the first day of the week
    const startDate = startOfWeek(new Date(year, 0, 1), { weekStartsOn: 1 });

    // Calculate the start date of the given week
    const weekStartDate = addDays(startDate, isoWeek * 7);

    // Initialize an array to store the days and dates
    const daysInWeek = [];

    // Loop through the days of the week and add them to the array
    for (let i = 0; i < 7; i++) {
      const currentDate = addDays(weekStartDate, i);
      daysInWeek.push(format(currentDate, "yyyy-MM-dd"));
    }

    setDaysinWeek(daysInWeek);
  }

  function get7DaysInWeek() {
    // Get the current date
    const currentDate = new Date();

    // Initialize an array to store the days and dates
    const daysInWeek = [];

    // Loop through the next 7 days and add them to the array
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() + i);
      daysInWeek.push(date.toISOString().slice(0, 10));
    }

    setDaysinWeek(daysInWeek);
  }

  const weekInputRef = useRef(null);

  const openDatePicker = () => {
    if (weekInputRef.current) {
      weekInputRef.current.showPicker();
    }
  };

  const { data: appointmentType } = useQuery(GET_APPOINTMENT_TYPE_TEMPLATES, {
    variables: {
      organizationId: parseInt(process.env.REACT_APP_ORGANIZATION_ID),
    },
  });

  const [getAvailableAnySlots, { data: availableAnyProviderSlots }] =
    useLazyQuery<{
      availableSlots: any[];
    }>(GET_CONSUMER_APPOINTMENT_EVENT_SLOTS);

  const [getAvailableSlots] = useLazyQuery(GET_AVAILABLE_SLOTS);

  useEffect(() => {
    if (appointmentType?.appointmentTypeTemplatesByOrganization) {
      const resultDate = new Date(daysinWeek[6]);
      resultDate.setDate(resultDate.getDate() + 7);
      const formattedResultDate = resultDate.toISOString().slice(0, 10);
      const availableSlotsAsync = async () => {
        const { data } = await getAvailableSlots({
          variables: {
            availabilityFilter: {
              appointmentTypeTemplateId:
                appointmentType?.appointmentTypeTemplatesByOrganization[0].id,
              fromDate: daysinWeek[0],
              toDate: formattedResultDate,
            },
          },
        });

        console.log("data", data);
        setAllServiceProviders(
          data?.availableSlotsWithServiceProviderInfo.serviceProviders
        );

        let availableSlotsData =
          data?.availableSlotsWithServiceProviderInfo.slots;

        if (!selectedProvider) return;

        if (selectedProvider === "anyProvider") {
          const { data: anyProviderSlots } = await getAvailableAnySlots({
            variables: {
              availabilityFilter: {
                appointmentTypeTemplateId: 197,
                fromDate: daysinWeek[0],
                toDate: daysinWeek[6],
                filter: {
                  region: patientLocation.split("-")[0].trim(),
                },
              },
            },
          });

          console.log("anyProviderSlots", anyProviderSlots);

          const sortedSlots = [...anyProviderSlots.availableSlots].sort(
            (p1, p2) => {
              const startDateP1 = new Date(p1.startDateTime);
              const startDateP2 = new Date(p2.startDateTime);
              return startDateP1 < startDateP2
                ? -1
                : startDateP1 > startDateP2
                ? 1
                : 0;
            }
          );

          availableSlotsData = sortedSlots;

          console.log("availableSlotsData - any", availableSlotsData);
          setAvailableSlots(availableSlotsData);
        } else {
          const selectedServiceProviderSlots = availableSlotsData.filter(
            (slot) => {
              return slot.serviceProviderId === selectedProvider.id;
            }
          );

          const sortedSlots = selectedServiceProviderSlots.sort((p1, p2) => {
            const startDateP1 = new Date(p1.startDateTime);
            const startDateP2 = new Date(p2.startDateTime);
            return startDateP1 < startDateP2
              ? -1
              : startDateP1 > startDateP2
              ? 1
              : 0;
          });

          availableSlotsData = sortedSlots;
          console.log("availableSlotsData", availableSlotsData);
          setAvailableSlots(availableSlotsData);
        }
      };

      availableSlotsAsync();
    }
  }, [appointmentType, daysinWeek, selectedProvider]);

  // populate date to slot dict - `dataSource`
  useEffect(() => {
    if (!availableSlots) return;
    if (!selectedDate) {
      const datetime = dateService.convertUTCDateToUserTimezone(
        availableSlots[0].date
      );
      const formattedDate = datetime.format(API_DATE_FORMAT);
      setSelectedDate(formattedDate);
    }

    const dataSource = {};
    availableSlots.forEach((slot) => {
      const browserDate = dateService.convertUtcDateTimeToBrowserFormat(
        slot.date,
        slot.startTime,
        "YYYYMMDD"
      );
      if (!dataSource[browserDate]) {
        dataSource[browserDate] = [];
      }
      dataSource[browserDate].push(slot);
    });

    setDataSource(dataSource);
  }, [availableSlots]);

  const [filteredSlots, setFilteredSlots] = useState<any>();
  useEffect(() => {
    // pass moment time to this function
    const renderTimeSlots = async (selectedDate: string) => {
      const momentObj = dateService.convertDateStrToMoment(
          selectedDate,
          API_DATE_FORMAT
        ),
        slots = dataSource?.[momentObj.format("YYYYMMDD")],
        isSlotSelected = function (slot: IEventSlot) {
          if (!selectedSlot) return false;
          return slot.startTime === selectedSlot.startTime;
        };

      if (!Array.isArray(slots) || !slots.length) {
        return <div></div>;
      }

      const populateSlots = async (slots, start, end) => {
        const availableSlots = await slots.reduce((acc, slot) => {
          const currentTime = dateService.convertToMomentObject(
            dateService.convertUtcDateTimeToBrowserFormat(
              slot.date,
              slot.startTime,
              USER_TIME_FORMAT_12H
            )
          );

          const startTime = dateService.convertToMomentObject(start);
          const endTime = dateService.convertToMomentObject(end);

          if (currentTime.isBetween(startTime, endTime)) {
            acc.push(
              <button
                key={uid()}
                onClick={() => setSelectedSlot(slot)}
                className={`w-full py-2.5 text-sm rounded-md hover:border-transparent focus:outline-none border  hover:border-primary-900 hover:bg-[#EAEBFF] hover:text-primary-900 hover:font-semibold duration-300 transition-colors group ${
                  isSlotSelected(slot)
                    ? " text-white font-semibold bg-primary"
                    : "text-black bg-white border border-black"
                }`}>
                {dateService.convertUtcDateTimeToBrowserFormat(
                  slot.date,
                  slot.startTime,
                  USER_TIME_FORMAT_12H
                )}
              </button>
            );
          }
          return acc;
        }, []);

        return availableSlots;
      };

      const morningSlots = await populateSlots(slots, "00:59 AM", "12:00 PM");
      const afternoonSlots = await populateSlots(slots, "11:59 AM", "04:00 PM");
      const eveningSlots = await populateSlots(slots, "03:59 PM", "11:59 PM");

      return (
        <>
          <div>
            {/* Morning Slots */}
            {morningSlots.length > 0 && (
              <SlotsTemplate
                title="Morning"
                slots={morningSlots}
                icon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.30125 13.125H13.6987C13.5525 12.2497 13.1006 11.4546 12.4234 10.8811C11.7461 10.3076 10.8874 9.99286 10 9.99286C9.11255 9.99286 8.25387 10.3076 7.57662 10.8811C6.89938 11.4546 6.44747 12.2497 6.30125 13.125ZM5 13.75C5 12.4239 5.52678 11.1521 6.46447 10.2145C7.40215 9.27678 8.67392 8.75 10 8.75C11.3261 8.75 12.5979 9.27678 13.5355 10.2145C14.4732 11.1521 15 12.4239 15 13.75C15 13.9158 14.9342 14.0747 14.8169 14.1919C14.6997 14.3092 14.5408 14.375 14.375 14.375H5.625C5.45924 14.375 5.30027 14.3092 5.18306 14.1919C5.06585 14.0747 5 13.9158 5 13.75ZM10 3.75C10.1658 3.75 10.3247 3.81585 10.4419 3.93306C10.5592 4.05027 10.625 4.20924 10.625 4.375V6.875C10.625 7.04076 10.5592 7.19973 10.4419 7.31694C10.3247 7.43415 10.1658 7.5 10 7.5C9.83424 7.5 9.67527 7.43415 9.55806 7.31694C9.44085 7.19973 9.375 7.04076 9.375 6.875V4.375C9.375 4.20924 9.44085 4.05027 9.55806 3.93306C9.67527 3.81585 9.83424 3.75 10 3.75ZM20 13.75C20 13.9158 19.9342 14.0747 19.8169 14.1919C19.6997 14.3092 19.5408 14.375 19.375 14.375H16.875C16.7092 14.375 16.5503 14.3092 16.4331 14.1919C16.3158 14.0747 16.25 13.9158 16.25 13.75C16.25 13.5842 16.3158 13.4253 16.4331 13.3081C16.5503 13.1908 16.7092 13.125 16.875 13.125H19.375C19.5408 13.125 19.6997 13.1908 19.8169 13.3081C19.9342 13.4253 20 13.5842 20 13.75ZM3.75 13.75C3.75 13.9158 3.68415 14.0747 3.56694 14.1919C3.44973 14.3092 3.29076 14.375 3.125 14.375H0.625C0.45924 14.375 0.300269 14.3092 0.183058 14.1919C0.065848 14.0747 0 13.9158 0 13.75C0 13.5842 0.065848 13.4253 0.183058 13.3081C0.300269 13.1908 0.45924 13.125 0.625 13.125H3.125C3.29076 13.125 3.44973 13.1908 3.56694 13.3081C3.68415 13.4253 3.75 13.5842 3.75 13.75ZM17.0712 6.67875C17.1884 6.79595 17.2542 6.9549 17.2542 7.12062C17.2542 7.28635 17.1884 7.4453 17.0712 7.5625L15.3038 9.33C15.2461 9.38969 15.1771 9.43731 15.1009 9.47006C15.0246 9.50282 14.9426 9.52006 14.8596 9.52078C14.7766 9.5215 14.6943 9.50569 14.6175 9.47426C14.5407 9.44284 14.4709 9.39643 14.4123 9.33775C14.3536 9.27906 14.3072 9.20928 14.2757 9.13247C14.2443 9.05566 14.2285 8.97336 14.2292 8.89037C14.2299 8.80739 14.2472 8.72537 14.2799 8.64912C14.3127 8.57287 14.3603 8.5039 14.42 8.44625L16.1875 6.67875C16.3047 6.56158 16.4636 6.49576 16.6294 6.49576C16.7951 6.49576 16.954 6.56158 17.0712 6.67875ZM5.58 9.33C5.4628 9.44717 5.30385 9.51299 5.13812 9.51299C4.9724 9.51299 4.81345 9.44717 4.69625 9.33L2.92875 7.5625C2.8149 7.44462 2.7519 7.28675 2.75333 7.12287C2.75475 6.959 2.82048 6.80224 2.93636 6.68636C3.05224 6.57048 3.209 6.50475 3.37287 6.50333C3.53675 6.5019 3.69462 6.5649 3.8125 6.67875L5.58 8.44625C5.69717 8.56345 5.76299 8.7224 5.76299 8.88812C5.76299 9.05385 5.69717 9.2128 5.58 9.33Z"
                      fill="black"
                    />
                  </svg>
                }
              />
            )}
            {/* Afternoon  Slots */}
            {afternoonSlots.length > 0 && (
              <SlotsTemplate
                title="Afternoon"
                slots={afternoonSlots}
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="none"
                    viewBox="0 0 20 20">
                    <g clipPath="url(#clip0_95_2227)">
                      <path
                        fill="#161616"
                        fillRule="evenodd"
                        d="M10 13.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5zM10 15a5 5 0 100-10 5 5 0 000 10zm0-15a.625.625 0 01.625.625v2.5a.625.625 0 11-1.25 0v-2.5A.625.625 0 0110 0zm0 16.25a.624.624 0 01.625.625v2.5a.624.624 0 11-1.25 0v-2.5A.625.625 0 0110 16.25zM20 10a.624.624 0 01-.625.625h-2.5a.624.624 0 110-1.25h2.5A.625.625 0 0120 10zM3.75 10a.625.625 0 01-.625.625h-2.5a.625.625 0 110-1.25h2.5A.625.625 0 013.75 10zm13.321-7.071a.625.625 0 010 .884L15.304 5.58a.626.626 0 01-.884-.885l1.768-1.767a.625.625 0 01.883 0zM5.58 14.42a.625.625 0 010 .884L3.812 17.07a.625.625 0 01-.883-.884l1.767-1.767a.625.625 0 01.884 0zm11.491 2.651a.625.625 0 01-.884 0l-1.767-1.767a.625.625 0 01.884-.884l1.767 1.768a.625.625 0 010 .883zM5.58 5.581a.625.625 0 01-.884 0L2.93 3.812a.625.625 0 11.884-.883L5.58 4.696a.625.625 0 010 .885z"
                        clipRule="evenodd"></path>
                    </g>
                    <defs>
                      <clipPath id="clip0_95_2227">
                        <path fill="#fff" d="M0 0H20V20H0z"></path>
                      </clipPath>
                    </defs>
                  </svg>
                }
              />
            )}
            {eveningSlots.length > 0 && (
              <SlotsTemplate
                title="Evening"
                slots={eveningSlots}
                icon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.30125 13.125H13.6987C13.5525 12.2497 13.1006 11.4546 12.4234 10.8811C11.7461 10.3076 10.8874 9.99286 10 9.99286C9.11255 9.99286 8.25387 10.3076 7.57662 10.8811C6.89938 11.4546 6.44747 12.2497 6.30125 13.125ZM5 13.75C5 12.4239 5.52678 11.1521 6.46447 10.2145C7.40215 9.27678 8.67392 8.75 10 8.75C11.3261 8.75 12.5979 9.27678 13.5355 10.2145C14.4732 11.1521 15 12.4239 15 13.75C15 13.9158 14.9342 14.0747 14.8169 14.1919C14.6997 14.3092 14.5408 14.375 14.375 14.375H5.625C5.45924 14.375 5.30027 14.3092 5.18306 14.1919C5.06585 14.0747 5 13.9158 5 13.75ZM10 3.75C10.1658 3.75 10.3247 3.81585 10.4419 3.93306C10.5592 4.05027 10.625 4.20924 10.625 4.375V6.875C10.625 7.04076 10.5592 7.19973 10.4419 7.31694C10.3247 7.43415 10.1658 7.5 10 7.5C9.83424 7.5 9.67527 7.43415 9.55806 7.31694C9.44085 7.19973 9.375 7.04076 9.375 6.875V4.375C9.375 4.20924 9.44085 4.05027 9.55806 3.93306C9.67527 3.81585 9.83424 3.75 10 3.75ZM20 13.75C20 13.9158 19.9342 14.0747 19.8169 14.1919C19.6997 14.3092 19.5408 14.375 19.375 14.375H16.875C16.7092 14.375 16.5503 14.3092 16.4331 14.1919C16.3158 14.0747 16.25 13.9158 16.25 13.75C16.25 13.5842 16.3158 13.4253 16.4331 13.3081C16.5503 13.1908 16.7092 13.125 16.875 13.125H19.375C19.5408 13.125 19.6997 13.1908 19.8169 13.3081C19.9342 13.4253 20 13.5842 20 13.75ZM3.75 13.75C3.75 13.9158 3.68415 14.0747 3.56694 14.1919C3.44973 14.3092 3.29076 14.375 3.125 14.375H0.625C0.45924 14.375 0.300269 14.3092 0.183058 14.1919C0.065848 14.0747 0 13.9158 0 13.75C0 13.5842 0.065848 13.4253 0.183058 13.3081C0.300269 13.1908 0.45924 13.125 0.625 13.125H3.125C3.29076 13.125 3.44973 13.1908 3.56694 13.3081C3.68415 13.4253 3.75 13.5842 3.75 13.75ZM17.0712 6.67875C17.1884 6.79595 17.2542 6.9549 17.2542 7.12062C17.2542 7.28635 17.1884 7.4453 17.0712 7.5625L15.3038 9.33C15.2461 9.38969 15.1771 9.43731 15.1009 9.47006C15.0246 9.50282 14.9426 9.52006 14.8596 9.52078C14.7766 9.5215 14.6943 9.50569 14.6175 9.47426C14.5407 9.44284 14.4709 9.39643 14.4123 9.33775C14.3536 9.27906 14.3072 9.20928 14.2757 9.13247C14.2443 9.05566 14.2285 8.97336 14.2292 8.89037C14.2299 8.80739 14.2472 8.72537 14.2799 8.64912C14.3127 8.57287 14.3603 8.5039 14.42 8.44625L16.1875 6.67875C16.3047 6.56158 16.4636 6.49576 16.6294 6.49576C16.7951 6.49576 16.954 6.56158 17.0712 6.67875ZM5.58 9.33C5.4628 9.44717 5.30385 9.51299 5.13812 9.51299C4.9724 9.51299 4.81345 9.44717 4.69625 9.33L2.92875 7.5625C2.8149 7.44462 2.7519 7.28675 2.75333 7.12287C2.75475 6.959 2.82048 6.80224 2.93636 6.68636C3.05224 6.57048 3.209 6.50475 3.37287 6.50333C3.53675 6.5019 3.69462 6.5649 3.8125 6.67875L5.58 8.44625C5.69717 8.56345 5.76299 8.7224 5.76299 8.88812C5.76299 9.05385 5.69717 9.2128 5.58 9.33Z"
                      fill="black"
                    />
                  </svg>
                }
              />
            )}
          </div>
        </>
      );
    };

    const getSlots = async () => {
      const _slots = await renderTimeSlots(selectedDate);
      setFilteredSlots(_slots);
    };

    getSlots();
  }, [dataSource, selectedDate, selectedSlot, availableSlots]);

  return (
    <div className="ml-20 max-lg:ml-0">
      <div className="text-sm text-black p-2 bg-bgLightBlue leading-6 items-center justify-between px-2 hidden max-lg:flex">
        <div>
          {patientLocation} | {preferredDate} <br /> {specialty} | {visitReason}
        </div>
        <button
          onClick={() => {
            navigate("/schedule");
          }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24">
            <g clipPath="url(#clip0_6080_53894)">
              <path
                fill="#3537E8"
                d="M5 19h1.414l9.314-9.314-1.414-1.414L5 17.586V19zm16 2H3v-4.243L16.435 3.322a1 1 0 011.414 0l2.829 2.83a1 1 0 010 1.413L9.243 19H21v2zM15.728 6.858l1.414 1.414 1.414-1.414-1.414-1.414-1.414 1.414z"></path>
            </g>
            <defs>
              <clipPath id="clip0_6080_53894">
                <path fill="#fff" d="M0 0H24V24H0z"></path>
              </clipPath>
            </defs>
          </svg>
        </button>
      </div>

      <div className="max-lg:hidden flex items-center justify-start mt-10">
        <div className="border rounded-l-lg border-lightBlue p-4 pr-8 text-lg">
          {visitType}
        </div>
        <div className="border border-lightBlue p-4 pr-8 text-lg">
          {patientLocation}
        </div>
        <div className="border border-lightBlue p-4 pr-8 text-lg">
          {preferredDate}
        </div>
        <div className="border border-lightBlue p-4 pr-8 text-lg">
          {specialty}
        </div>
        <div className="border rounded-r-lg border-lightBlue p-4 pr-8 text-lg">
          {visitReason}
        </div>
      </div>

      <div className="flex flex-col w-1/2 max-lg:w-full">
        <div className="p-2">
          <Listbox value={selectedProvider} onChange={setSelectedProvider}>
            {({ open }) => (
              <>
                <Listbox.Label className="block text-sm font-medium leading-6 text-gray-900">
                  Select Provider
                </Listbox.Label>
                <div className="relative mt-2">
                  <Listbox.Button className="relative w-4/5 max-lg:w-full cursor-default rounded-md bg-white py-3 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm sm:leading-6">
                    <span className="flex items-center">
                      <span className=" block truncate">
                        {selectedProvider
                          ? selectedProvider === "anyProvider"
                            ? "Any Provider"
                            : `${selectedProvider.firstName as string} ${
                                selectedProvider.lastName as string
                              }`
                          : "Select Provider"}
                      </span>
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-6 h-6">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                        />
                      </svg>
                    </span>
                  </Listbox.Button>
                  <Transition
                    show={open}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <Listbox.Options className="absolute border border-black z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      <Listbox.Option
                        key={0}
                        className={({ active }) =>
                          classNames(
                            active
                              ? "bg-indigo-600 text-white"
                              : "text-gray-900",
                            "relative cursor-default select-none py-2 pl-3 pr-9"
                          )
                        }
                        value="anyProvider">
                        {({ selected, active }) => (
                          <>
                            <div className="flex items-center">
                              <span
                                className={classNames(
                                  selected ? "font-semibold" : "font-normal",
                                  " block truncate"
                                )}>
                                Any Provider
                              </span>
                            </div>

                            {selected ? (
                              <span
                                className={classNames(
                                  active ? "text-white" : "text-indigo-600",
                                  "absolute inset-y-0 right-0 flex items-center pr-4"
                                )}>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="w-6 h-6">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M4.5 12.75l6 6 9-13.5"
                                  />
                                </svg>
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>

                      {allServiceProviders
                        ? allServiceProviders.map((person) => (
                            <Listbox.Option
                              key={person.id}
                              className={({ active }) =>
                                classNames(
                                  active
                                    ? "bg-indigo-600 text-white"
                                    : "text-gray-900",
                                  "relative cursor-default select-none py-2 pl-3 pr-9"
                                )
                              }
                              value={person}>
                              {({ selected, active }) => (
                                <>
                                  <div className="flex items-center">
                                    <span
                                      className={classNames(
                                        selected
                                          ? "font-semibold"
                                          : "font-normal",
                                        " block truncate"
                                      )}>
                                      {person.firstName} {person.lastName}
                                    </span>
                                  </div>

                                  {selected ? (
                                    <span
                                      className={classNames(
                                        active
                                          ? "text-white"
                                          : "text-indigo-600",
                                        "absolute inset-y-0 right-0 flex items-center pr-4"
                                      )}>
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="w-6 h-6">
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M4.5 12.75l6 6 9-13.5"
                                        />
                                      </svg>
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Listbox.Option>
                          ))
                        : null}
                    </Listbox.Options>
                  </Transition>
                </div>
              </>
            )}
          </Listbox>
        </div>
        {selectedProvider && (
          <div className="mt-4">
            <div className="flex items-center justify-between p-2">
              <div className="text-xs">Provider’s Availability</div>
              <div className="text-xs">
                <button
                  className="flex gap-1 items-center"
                  onClick={openDatePicker}>
                  <div className="text-primary">
                    {week && format(new Date(daysinWeek[0]), "MMMM yyyy")}
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="w-5 h-5">
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                <label htmlFor="">
                  <input
                    type="week"
                    className="w-0 h-0 invisible absolute "
                    name=""
                    id=""
                    ref={weekInputRef}
                    onChange={(e) => {
                      onChange(week);
                      getDaysInWeek(e.target.value);
                      setSelectedDate(daysinWeek[0]);
                    }}
                  />
                </label>
              </div>
            </div>
            <div className="flex gap-2 p-2">
              {daysinWeek.map((day) => (
                <button
                  key={day}
                  onClick={() => setSelectedDate(day)}
                  disabled={
                    // when length of availableSlots is zero on that day

                    !availableSlots?.some(
                      (slot) =>
                        dateService.convertUtcDateTimeToBrowserFormat(
                          slot.date,
                          slot.startTime,
                          "YYYYMMDD"
                        ) ===
                        dateService.convertUtcDateTimeToBrowserFormat(
                          day,
                          "00:00 AM",
                          "YYYYMMDD"
                        )
                    )
                  }
                  className={`${
                    selectedDate === day
                      ? "bg-primary text-white border-none"
                      : "disabled:bg-bgLightBlue disabled:text-lightBlue disabled:border-lightBlue disabled:border bg-white text-black border border-black"
                  }  border rounded  flex flex-col gap-2 justify-center items-center p-2 flex-1`}>
                  <div>{format(new Date(day), "EEEE").slice(0, 1)}</div>
                  <div className="max-md:hidden">
                    {format(new Date(day), "dd MMM")}
                  </div>
                  <div className="hidden max-md:block">
                    {format(new Date(day), "dd")}
                  </div>
                </button>
              ))}
            </div>
            <main className="mt-4 bg-bgLightBlue rounded-md max-lg:rounded-none px-2 max-lg:px-0 mb-40 py-4 ">
              {filteredSlots}
            </main>
          </div>
        )}
      </div>
      <div className="w-full flex max-lg:justify-center justify-start items-center p-2 fixed bottom-0 bg-white">
        <button
          disabled={!selectedSlot}
          onClick={handleProceed}
          className="w-fit max-lg:w-full disabled:opacity-60 bg-black px-12 py-4 my-4 text-white rounded-md font-bold disabled:bg-gray-400 disabled:cursor-not-allowed max-lg:px-16 max-sm:px-4">
          Proceed with selected date and time
        </button>
      </div>
    </div>
  );
};

const SlotsTemplate = ({ title, slots, icon }) => {
  return (
    <>
      <div className="flex gap-x-2 items-center px-2">
        {icon}
        <h1>{title}</h1>
      </div>
      <div className="grid grid-cols-5 px-2 gap-4 mt-4 mb-4  max-sm:grid-cols-3">
        {slots}
      </div>
    </>
  );
};

export default ProviderResult;
